import React, { useEffect } from 'react';
import { Card, Typography, Box, CircularProgress, Alert, Grid } from '@mui/material';
import { 
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useOwner } from '../../contexts/OwnerContext';

interface DashboardMetric {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const Dashboard: React.FC = () => {
  const { 
    metrics, 
    recentActivity, 
    loading, 
    errors, 
    loadDashboardMetrics 
  } = useOwner();

  useEffect(() => {
    loadDashboardMetrics();
  }, [loadDashboardMetrics]);

  if (loading.dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errors.dashboard) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading dashboard: {errors.dashboard}
      </Alert>
    );
  }

  const dashboardMetrics: DashboardMetric[] = [
    {
      title: 'Total Clients',
      value: metrics?.totalTenants || 0,
      icon: <PeopleIcon />,
      color: '#1976d2'
    },
    {
      title: 'Active Clients',
      value: metrics?.activeTenants || 0,
      icon: <PeopleIcon />,
      color: '#2e7d32'
    },
    {
      title: 'Monthly Revenue',
      value: `$${(metrics?.monthlyRevenue || 0).toLocaleString()}`,
      icon: <MoneyIcon />,
      color: '#2e7d32'
    },
    {
      title: 'Storage Usage',
      value: `${(metrics?.totalStorageUsed || 0).toFixed(1)} GB`,
      icon: <StorageIcon />,
      color: '#ed6c02'
    },
    {
      title: 'System Health',
      value: metrics?.systemHealth ? `${metrics.systemHealth.cpuUsage.toFixed(1)}% CPU` : 'N/A',
      icon: <SpeedIcon />,
      color: '#9c27b0'
    },
    {
      title: 'Active Issues',
      value: metrics?.activeIssues || 0,
      icon: <WarningIcon />,
      color: '#d32f2f'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Owner Dashboard
      </Typography>
      
      {/* Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Card 
              sx={{ 
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: 'background.paper',
                boxShadow: 2
              }}
            >
              <Box 
                sx={{ 
                  color: metric.color,
                  mb: 1
                }}
              >
                {metric.icon}
              </Box>
              <Typography variant="h6" component="div">
                {metric.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {metric.title}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {/* Activity list will go here */}
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            {/* System status will go here */}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 